import React from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { useLandingTheme } from "../../LandingThemeContext";
import { ThemedBody, ThemedContainer, ThemedDisplayTitle } from "../themed/ThemedLayout";
import { SlideToQuestion } from "./SlideToQuestion";
import { UploadBar } from "./UploadBar";

const HANDWRITING = '"Caveat", cursive';

// A strip of masking tape holding a specimen to the page.
function Tape({ rotate, ...props }: { rotate: string } & React.ComponentProps<typeof Box>) {
  return (
    <Box
      aria-hidden="true"
      position="absolute"
      w="110px"
      h="28px"
      bg="rgba(236, 224, 176, 0.85)"
      boxShadow="0 1px 2px rgba(0,0,0,0.12)"
      transform={`rotate(${rotate})`}
      sx={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.18) 0 3px, transparent 3px 7px)" }}
      {...props}
    />
  );
}

// A note in the botanist's hand.
export function Handwritten({ children, ...props }: React.ComponentProps<typeof Text>) {
  const theme = useLandingTheme();
  return (
    <Text fontFamily={HANDWRITING} fontSize="24px" lineHeight={1.1} color={theme.color.accentDark} {...props}>
      {children}
    </Text>
  );
}

// A rough underline stroke, drawn by hand.
function MarkerUnderline() {
  const theme = useLandingTheme();
  return (
    <Box as="svg" viewBox="0 0 420 22" position="absolute" left="-2%" bottom="-8px" w="104%" h="22px" aria-hidden="true" preserveAspectRatio="none">
      <path d="M4 15 C 80 6, 160 18, 240 10 S 380 4, 416 12" fill="none" stroke={theme.color.highlight} strokeWidth="9" strokeLinecap="round" opacity="0.95" />
    </Box>
  );
}

// The botanist's notebook: cream paper, a pressed fern in the margin, a hand-written note pointing at
// the specimen — a lecture slide taped to the page with the question it produced.
export function FieldGuideHero() {
  const theme = useLandingTheme();
  return (
    <Box as="section" position="relative" overflow="hidden" mt="-96px" pt={{ base: "132px", lg: "150px" }} pb={{ base: "48px", lg: "140px" }} minH={{ lg: "860px" }} display="flex" flexDirection="column" justifyContent="center">
      <Box aria-hidden="true" position="absolute" inset={0} bgImage="url(/images/landing/generated/paper-field-notebook.jpg)" bgSize="cover" bgPosition="center" opacity={0.9} />
      {/* Pressed fern in the outer margin */}
      <Image
        aria-hidden="true"
        src="/images/landing/generated/plate-fern-specimen.png"
        alt=""
        position="absolute"
        right={{ base: "-140px", lg: "-60px" }}
        top="-40px"
        w={{ base: "280px", lg: "380px" }}
        opacity={0.55}
        transform="rotate(14deg)"
        pointerEvents="none"
        display={{ base: "none", md: "block" }}
      />

      <ThemedContainer position="relative">
        <Flex direction={{ base: "column", lg: "row" }} align={{ base: "stretch", lg: "center" }} gap={{ base: "56px", lg: "72px" }}>
          <Box flex="1" w="100%">
            <Handwritten mb="14px" color={theme.color.muted}>
              Field notes · from your slides, PDFs and videos
            </Handwritten>
            <ThemedDisplayTitle level="hero" mb="24px" fontSize={{ base: "44px", md: "60px", lg: "70px" }}>
              <Box as="span" position="relative" display="inline-block">
                Insanely good
                <MarkerUnderline />
              </Box>
              <Box as="br" display={{ base: "none", lg: "inline" }} />
              multiple choice <Box as="br" display={{ base: "none", lg: "inline" }} />questions, made <Box as="br" display={{ base: "none", lg: "inline" }} />in seconds.
            </ThemedDisplayTitle>
            <ThemedBody mb="36px" maxW="480px">
              Upload what you're studying and practice on questions that are relevant, clear, and hard to guess.
            </ThemedBody>
            <UploadBar maxW="540px" />
          </Box>

          <Box flex="1" w="100%" maxW={{ base: "560px", lg: "none" }} position="relative" pt="10px">
            {/* The specimen, taped down */}
            <Box position="relative" transform={{ lg: "rotate(-1.5deg)" }}>
              <SlideToQuestion />
              <Tape rotate="-6deg" top="-12px" left="120px" />
              <Tape rotate="5deg" top="132px" right="-30px" />
            </Box>
            {/* The note, with an arrow to the specimen */}
            <Box position="absolute" left={{ base: "0", lg: "-10px" }} top="calc(100% + 26px)" maxW="300px" display={{ base: "none", md: "block" }}>
              <Box as="svg" viewBox="0 0 120 70" w="120px" h="70px" position="absolute" top="-56px" left="300px" aria-hidden="true">
                <path d="M6 66 C 40 60, 80 40, 108 6" fill="none" stroke={theme.color.accentDark} strokeWidth="2" strokeLinecap="round" />
                <path d="M92 8 L110 4 L110 22" fill="none" stroke={theme.color.accentDark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </Box>
              <Handwritten>
                specimen no. 14 — one slide, one question.
                <br />
                relevance 99 / 100, wrong answers scored 91.
              </Handwritten>
            </Box>
          </Box>
        </Flex>
      </ThemedContainer>
    </Box>
  );
}
